import { Alert } from '@atoms'
import { MultiSelectField, type MultiSelectFieldOption } from '@components/molecules/MultiSelectField'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { scopedTranslation } from '@utils/I18n'

import { MAX_LIST_SIZE, type EmbedConfig } from '@utils/embeds/embedConfig'
import type { FeedStatus } from '@utils/embeds/eventsFeed'

const t = scopedTranslation('embeds.events_map.events')

export type EventFilterOptions = {
  groups: Array<{ id: number; name: string; eventCount: number }>
  eventTypes: Array<{ id: number; name: string }>
  tags: Array<{ slug: string; name: string }>
}

type Filters = Pick<EmbedConfig, 'groupIds' | 'eventTypeIds' | 'tags'>

type EventFiltersProps = {
  filtered: boolean
  onFilteredChange: (filtered: boolean) => void
  filters: Filters
  onFiltersChange: (filters: Partial<Filters>) => void
  options: EventFilterOptions
  upcomingEventCount: number
  status: FeedStatus
  matchCount: number
}

const asStrings = (ids: number[]) => ids.map(String)
const asIds = (values: string[]) => values.map(Number)

function upToLimit(options: MultiSelectFieldOption[], value: string[]) {
  const full = value.length >= MAX_LIST_SIZE
  return options.map((option) => ({ ...option, disabled: full && !value.includes(option.value) }))
}

type FilterPickerProps = {
  label: string
  placeholder: string
  searchPlaceholder: string
  value: string[]
  onValueChange: (value: string[]) => void
  options: MultiSelectFieldOption[]
}

function FilterPicker({ label, placeholder, searchPlaceholder, value, onValueChange, options }: FilterPickerProps) {
  return (
    <MultiSelectField
      label={label}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      noResultsText={t('no_results')}
      value={value}
      onValueChange={onValueChange}
      options={upToLimit(options, value)}
    />
  )
}

export function EventFilters({
  filtered,
  onFilteredChange,
  filters,
  onFiltersChange,
  options,
  upcomingEventCount,
  status,
  matchCount,
}: EventFiltersProps) {
  if (upcomingEventCount === 0)
    return (
      <Alert type="info" size="sm" className="tw:text-xs">
        {t('none_yet')}
      </Alert>
    )

  const groupIds = asStrings(filters.groupIds)
  const eventTypeIds = asStrings(filters.eventTypeIds)
  const atLimit = [groupIds, eventTypeIds, filters.tags].some((value) => value.length >= MAX_LIST_SIZE)
  const counted = filtered && status === 'ready'

  return (
    <>
      <RadioGroupField<'all' | 'filtered'>
        label={t('which')}
        value={filtered ? 'filtered' : 'all'}
        onValueChange={(value) => onFilteredChange(value === 'filtered')}
        options={[
          { value: 'all', label: t('all') },
          {
            value: 'filtered',
            label: t('filtered'),
            description: t('filtered_hint'),
            whenSelected: (
              <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:p-3">
                {options.groups.length > 0 && (
                  <FilterPicker
                    label={t('groups')}
                    placeholder={t('any_group')}
                    searchPlaceholder={t('search_groups')}
                    value={groupIds}
                    onValueChange={(next) => onFiltersChange({ groupIds: asIds(next) })}
                    options={options.groups.map((group) => ({
                      value: String(group.id),
                      label: group.name,
                      description: t('group_event_count', { count: group.eventCount }),
                    }))}
                  />
                )}
                {options.eventTypes.length > 0 && (
                  <FilterPicker
                    label={t('event_types')}
                    placeholder={t('any_event_type')}
                    searchPlaceholder={t('search_event_types')}
                    value={eventTypeIds}
                    onValueChange={(next) => onFiltersChange({ eventTypeIds: asIds(next) })}
                    options={options.eventTypes.map((type) => ({ value: String(type.id), label: type.name }))}
                  />
                )}
                {options.tags.length > 0 ? (
                  <FilterPicker
                    label={t('tags')}
                    placeholder={t('any_tag')}
                    searchPlaceholder={t('search_tags')}
                    value={filters.tags}
                    onValueChange={(next) => onFiltersChange({ tags: next })}
                    options={options.tags.map((tag) => ({ value: tag.slug, label: tag.name }))}
                  />
                ) : (
                  <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">{t('no_tags')}</p>
                )}
                {atLimit && (
                  <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">{t('limit', { max: MAX_LIST_SIZE })}</p>
                )}
              </div>
            ),
          },
        ]}
      />
      <p className="tw:m-0 tw:text-xs tw:text-gray-true-500" aria-live="polite">
        {filtered && status === 'loading' && t('counting')}
        {counted && t('match_count', { count: matchCount, total: upcomingEventCount })}
      </p>
      {counted && matchCount === 0 && (
        <Alert type="warning" size="sm" className="tw:text-xs">
          {t('no_matches')}
        </Alert>
      )}
    </>
  )
}
